import type { Icon } from "@phosphor-icons/react";
import {
  AddressBook,
  Briefcase,
  CalendarBlank,
  ChartBar,
  ChatCircle,
  CheckSquare,
  Envelope,
  FileText,
  Globe,
  Megaphone,
  Pulse,
  Receipt,
  Robot,
  Signature,
  Ticket,
  TreeStructure,
} from "@phosphor-icons/react";

/**
 * Installable modules (Phase 2, docs/plans/MULTI-TENANT-MODULES.md).
 * A company uses a module when its row in organization_modules is enabled
 * (core modules are always on); members still need the role/capability.
 * Ids must match public.all_module_ids() in the database.
 */
export const MODULE_IDS = [
  "accounts",
  "pipeline",
  "tasks",
  "calendar",
  "tickets",
  "messages",
  "mail",
  "invoices",
  "proposals",
  "contracts",
  "marketing",
  "forms",
  "reports",
  "web_monitor",
  "hostinger",
  "assistant",
] as const;

export type ModuleId = (typeof MODULE_IDS)[number];

export type IntegrationId =
  | "stripe"
  | "sms"
  | "mail_account"
  | "ticket_inbox"
  | "hostinger";

export type ModuleOption = {
  key: string;
  label: string;
  description: string;
};

export const MODULE_CATEGORIES = [
  { id: "clients", label: "Clients & sales" },
  { id: "operations", label: "Operations" },
  { id: "communication", label: "Communication" },
  { id: "billing", label: "Billing" },
  { id: "marketing", label: "Marketing" },
  { id: "insights", label: "Insights & AI" },
] as const;

export type ModuleCategoryId = (typeof MODULE_CATEGORIES)[number]["id"];

/**
 * A function inside a module that not every business uses (e.g. charging
 * for tickets). Off for new companies, on for the home org (LBS); can be
 * switched on for any company. Stored in organization_modules.options.
 */
export type ModuleFeature = {
  key: string;
  label: string;
  description: string;
  /** Other modules the feature needs (it stays off while they are off). */
  requires?: ModuleId[];
};

/**
 * Something the app does on its own (reminders, receipts, texts). Stored in
 * organization_modules.options.automations[key].enabled; when a company
 * has no value yet, `defaultOn` applies (the home org has explicit values).
 * Mirror keys/defaults in supabase/functions/_shared/orgAutomations.ts.
 */
export type ModuleAutomation = {
  key: string;
  label: string;
  description: string;
  defaultOn: boolean;
  /** Apps it needs (e.g. Messages for texts); off while they are off. */
  requires?: ModuleId[];
};

export type NomiModule = {
  id: ModuleId;
  name: string;
  description: string;
  icon: Icon;
  /** Group in Apps and the platform console. */
  category: ModuleCategoryId;
  /** Always on; cannot be switched off. */
  core?: boolean;
  dependsOn: ModuleId[];
  /** Shown as a setup checklist when the module is enabled. */
  requiresIntegrations: IntegrationId[];
  /** In-app route prefixes owned by the module (guarded when it is off). */
  routes: string[];
  options?: ModuleOption[];
  features?: ModuleFeature[];
  automations?: ModuleAutomation[];
};

export const MODULES: Record<ModuleId, NomiModule> = {
  accounts: {
    id: "accounts",
    category: "clients",
    name: "Accounts",
    description: "Clients and the people you work with.",
    icon: AddressBook,
    core: true,
    dependsOn: [],
    requiresIntegrations: [],
    routes: ["/accounts", "/clients", "/companies", "/contacts", "/people"],
    options: [
      {
        key: "peopleOnly",
        label: "People only",
        description: "Hide company accounts; keep a list of people.",
      },
    ],
  },
  tasks: {
    id: "tasks",
    category: "operations",
    name: "Tasks",
    description: "To-dos with due dates, reminders and assignees.",
    icon: CheckSquare,
    core: true,
    dependsOn: [],
    requiresIntegrations: [],
    routes: ["/tasks"],
  },
  pipeline: {
    id: "pipeline",
    category: "clients",
    name: "Pipeline & projects",
    description: "Leads pipeline and project boards.",
    icon: Briefcase,
    dependsOn: ["accounts"],
    requiresIntegrations: [],
    routes: ["/leads", "/deals", "/projects"],
  },
  calendar: {
    id: "calendar",
    category: "operations",
    name: "Calendar",
    description: "Meetings, booking links and reminders.",
    icon: CalendarBlank,
    dependsOn: ["accounts"],
    requiresIntegrations: [],
    routes: ["/meetings", "/calendar"],
    automations: [
      {
        key: "bookingReminders",
        label: "Booking reminders",
        description:
          "Text people who booked through a booking link before their appointment.",
        defaultOn: false,
        requires: ["messages"],
      },
    ],
  },
  tickets: {
    id: "tickets",
    category: "operations",
    name: "Tickets",
    description: "Shared support inbox with replies and templates.",
    icon: Ticket,
    dependsOn: ["accounts"],
    requiresIntegrations: ["ticket_inbox"],
    routes: ["/tickets"],
    features: [
      {
        key: "addressSubjects",
        label: "Job-site address subjects",
        description: "Suggest Google addresses for the subject of new tickets.",
      },
      {
        key: "projectLinks",
        label: "Link tickets to projects",
        description: "Attach a ticket to a project from Pipeline & projects.",
        requires: ["pipeline"],
      },
      {
        key: "chargeForTickets",
        label: "Charge for tickets",
        description:
          "Invoice from a ticket and deliver files once the client pays.",
        requires: ["invoices"],
      },
    ],
  },
  messages: {
    id: "messages",
    category: "communication",
    name: "Messages",
    description: "SMS and calls with clients.",
    icon: ChatCircle,
    dependsOn: ["accounts"],
    requiresIntegrations: ["sms"],
    routes: ["/messages"],
  },
  mail: {
    id: "mail",
    category: "communication",
    name: "Mail",
    description: "Your Gmail or Outlook inbox inside Sigma.",
    icon: Envelope,
    dependsOn: [],
    requiresIntegrations: ["mail_account"],
    routes: ["/mail"],
  },
  invoices: {
    id: "invoices",
    category: "billing",
    name: "Invoices",
    description: "Invoices, payments and subscriptions.",
    icon: Receipt,
    dependsOn: ["accounts"],
    requiresIntegrations: ["stripe"],
    routes: ["/billing", "/payments"],
    features: [
      {
        key: "subscriptions",
        label: "Subscriptions & agreements",
        description:
          "Recurring billing with a service agreement the client signs. Uses your own agreement terms.",
      },
      {
        key: "paymentPlans",
        label: "Deposits & payment plans",
        description:
          "Take a deposit up front and charge the rest to the saved card on a schedule.",
      },
      {
        key: "autopay",
        label: "Autopay overdue invoices",
        description:
          "Charge the client's saved card when an invoice is 3 days overdue.",
      },
    ],
    automations: [
      {
        key: "paymentReminders",
        label: "Payment reminders",
        description:
          "Email the client 3 days and 1 day before the due date, on the due date and when overdue.",
        defaultOn: true,
      },
      {
        key: "textInvoices",
        label: "Text invoices too",
        description:
          "When you send an invoice, also text the payment link (you can still untick it).",
        defaultOn: false,
        requires: ["messages"],
      },
      {
        key: "textReceipts",
        label: "Text payment receipts",
        description:
          "Text the client when a payment comes in (email always goes).",
        defaultOn: false,
        requires: ["messages"],
      },
    ],
  },
  proposals: {
    id: "proposals",
    category: "clients",
    name: "Proposals",
    description: "Proposals with e-signature and deposits.",
    icon: FileText,
    dependsOn: ["accounts", "pipeline"],
    requiresIntegrations: [],
    routes: ["/billing", "/proposals"],
  },
  contracts: {
    id: "contracts",
    category: "clients",
    name: "Contracts",
    description: "Contracts and signed agreements.",
    icon: Signature,
    dependsOn: ["accounts"],
    requiresIntegrations: [],
    routes: ["/contracts"],
  },
  marketing: {
    id: "marketing",
    category: "marketing",
    name: "Marketing",
    description: "Email and SMS campaigns.",
    icon: Megaphone,
    dependsOn: ["accounts"],
    requiresIntegrations: [],
    routes: ["/marketing"],
  },
  forms: {
    id: "forms",
    category: "marketing",
    name: "Forms",
    description: "Public forms that create leads and requests.",
    icon: TreeStructure,
    dependsOn: [],
    requiresIntegrations: [],
    routes: ["/forms", "/forms-v2", "/web-forms"],
  },
  reports: {
    id: "reports",
    category: "insights",
    name: "Reports",
    description: "Reports for each app your workspace uses.",
    icon: ChartBar,
    core: true,
    dependsOn: [],
    requiresIntegrations: [],
    routes: ["/reports"],
  },
  web_monitor: {
    id: "web_monitor",
    category: "operations",
    name: "Web Monitor",
    description: "Website uptime and audit reports.",
    icon: Pulse,
    dependsOn: ["accounts"],
    requiresIntegrations: [],
    routes: ["/web-monitor"],
  },
  hostinger: {
    id: "hostinger",
    category: "operations",
    name: "Hostinger",
    description: "Domains and hosting from Hostinger.",
    icon: Globe,
    dependsOn: ["accounts"],
    requiresIntegrations: ["hostinger"],
    routes: ["/hostinger"],
  },
  assistant: {
    id: "assistant",
    category: "insights",
    name: "Ask Sigma",
    description: "AI assistant over your CRM data.",
    icon: Robot,
    dependsOn: [],
    requiresIntegrations: [],
    routes: [],
  },
};

export const MODULE_LIST: NomiModule[] = MODULE_IDS.map((id) => MODULES[id]);

export const INTEGRATION_LABELS: Record<IntegrationId, string> = {
  stripe: "Stripe",
  sms: "SMS provider (Twilio or Telnyx)",
  mail_account: "A mail account",
  ticket_inbox: "A support inbox",
  hostinger: "Hostinger API",
};

/** Module that owns an in-app path, if any. */
export const moduleForPath = (pathname: string): ModuleId | null => {
  for (const module of MODULE_LIST) {
    if (
      module.routes.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
      )
    ) {
      return module.id;
    }
  }
  return null;
};

/** Enabled module ids + options, as loaded into the member identity. */
export type OrgModulesState = {
  enabled: ModuleId[];
  options: Partial<Record<ModuleId, Record<string, unknown>>>;
};

export const isModuleEnabled = (
  state: OrgModulesState | null | undefined,
  moduleId: ModuleId,
) =>
  MODULES[moduleId].core === true ||
  // Before modules load (or for old cached identities) keep everything on.
  state == null ||
  state.enabled.includes(moduleId);

export const moduleOption = (
  state: OrgModulesState | null | undefined,
  moduleId: ModuleId,
  key: string,
) => state?.options[moduleId]?.[key] === true;

/** Feature definition, if the module declares it. */
export const moduleFeatureDefinition = (moduleId: ModuleId, key: string) =>
  MODULES[moduleId].features?.find((feature) => feature.key === key) ?? null;

/**
 * Is a module feature usable for the company: the module and every module
 * the feature needs are on, and the feature is switched on.
 */
export const isModuleFeatureEnabled = (
  state: OrgModulesState | null | undefined,
  moduleId: ModuleId,
  key: string,
) => {
  if (!isModuleEnabled(state, moduleId)) return false;
  const feature = moduleFeatureDefinition(moduleId, key);
  if (feature?.requires?.some((id) => !isModuleEnabled(state, id))) {
    return false;
  }
  // Before modules load (or old cached identities) keep everything on.
  return state == null || moduleOption(state, moduleId, key);
};

/** Saved value of an automation, or its default for the company. */
export const isModuleAutomationEnabled = (
  state: OrgModulesState | null | undefined,
  moduleId: ModuleId,
  key: string,
) => {
  if (!isModuleEnabled(state, moduleId)) return false;
  const automation = MODULES[moduleId].automations?.find(
    (entry) => entry.key === key,
  );
  if (!automation) return false;
  if (automation.requires?.some((id) => !isModuleEnabled(state, id))) {
    return false;
  }
  const saved = (
    state?.options[moduleId]?.automations as
      | Record<string, { enabled?: unknown }>
      | undefined
  )?.[key]?.enabled;
  return typeof saved === "boolean" ? saved : automation.defaultOn;
};
